/**
 * Frontend Environment Variable Validation Utility
 * 
 * This module provides validation for Vue.js environment variables
 * ensuring all required configuration is present and valid.
 */

/**
 * Validation rule types for environment variables
 */
const VALIDATION_TYPES = {
  STRING: 'string',
  URL: 'url',
  ENUM: 'enum'
};

/**
 * Environment types
 */
const ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  PRODUCTION: 'production',
  TEST: 'test'
};

/**
 * Frontend environment variable schema
 */
const FRONTEND_SCHEMA = {
  // API Configuration
  VUE_APP_API_URL: {
    type: VALIDATION_TYPES.URL,
    required: true,
    description: 'Backend API base URL',
    default: 'http://localhost:3000',
    validation: {
      protocol: ['http:', 'https:']
    },
    environments: ['development', 'production', 'test']
  },

  VUE_APP_API_KEY: {
    type: VALIDATION_TYPES.STRING,
    required: true,
    description: 'API key for backend authentication',
    validation: {
      minLength: 8,
      pattern: /^[A-Za-z0-9\-_]+$/
    },
    environments: ['development', 'production', 'test']
  },

  // Analytics Configuration
  VUE_APP_GA_ID: {
    type: VALIDATION_TYPES.STRING,
    required: false,
    description: 'Google Analytics tracking ID',
    validation: {
      pattern: /^G-[A-Z0-9]+$/
    },
    environments: ['production']
  },

  // Environment Configuration
  VUE_APP_ENV: {
    type: VALIDATION_TYPES.ENUM,
    required: false,
    default: 'development',
    description: 'Frontend environment indicator',
    validation: {
      enum: ['development', 'production', 'test']
    },
    environments: ['development', 'production', 'test']
  },

  // Build Configuration
  NODE_ENV: {
    type: VALIDATION_TYPES.ENUM,
    required: false,
    default: 'development',
    description: 'Node.js environment (auto-set by Vue CLI)',
    validation: {
      enum: ['development', 'production', 'test']
    },
    environments: ['development', 'production', 'test']
  }
};

/**
 * Environment-specific schemas
 */
const ENVIRONMENT_SCHEMAS = {
  [ENVIRONMENTS.DEVELOPMENT]: {
    ...FRONTEND_SCHEMA,
    // Development specific overrides
    VUE_APP_API_KEY: {
      ...FRONTEND_SCHEMA.VUE_APP_API_KEY,
      required: false,
      default: 'dev-api-key-12345'
    }
  },

  [ENVIRONMENTS.PRODUCTION]: {
    ...FRONTEND_SCHEMA,
    // Production requires all security variables
    VUE_APP_GA_ID: {
      ...FRONTEND_SCHEMA.VUE_APP_GA_ID,
      required: true
    }
  },

  [ENVIRONMENTS.TEST]: {
    ...FRONTEND_SCHEMA,
    // Test environment defaults
    VUE_APP_API_KEY: {
      ...FRONTEND_SCHEMA.VUE_APP_API_KEY,
      required: false,
      default: 'test-api-key-12345'
    },
    VUE_APP_API_URL: {
      ...FRONTEND_SCHEMA.VUE_APP_API_URL,
      required: false,
      default: 'http://localhost:3001'
    }
  }
};

/**
 * Individual variable validation functions
 */
const validators = {
  [VALIDATION_TYPES.STRING]: (value, rules = {}) => {
    if (typeof value !== 'string') {
      return { valid: false, error: 'Must be a string' };
    }

    if (rules.minLength && value.length < rules.minLength) {
      return { valid: false, error: `Must be at least ${rules.minLength} characters long` };
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      return { valid: false, error: `Must be at most ${rules.maxLength} characters long` };
    }

    if (rules.pattern && !rules.pattern.test(value)) {
      return { valid: false, error: `Must match expected format` };
    }

    return { valid: true };
  },

  [VALIDATION_TYPES.URL]: (value, rules = {}) => {
    try {
      const url = new URL(value);
      
      if (rules.protocol && !rules.protocol.includes(url.protocol)) {
        return { 
          valid: false, 
          error: `Protocol must be one of: ${rules.protocol.join(', ')}` 
        };
      }

      return { valid: true };
    } catch (error) {
      return { valid: false, error: 'Must be a valid URL' };
    }
  },

  [VALIDATION_TYPES.ENUM]: (value, rules = {}) => {
    if (!rules.enum || !Array.isArray(rules.enum)) {
      return { valid: false, error: 'Invalid enum configuration' };
    }

    if (!rules.enum.includes(value)) {
      return { 
        valid: false, 
        error: `Must be one of: ${rules.enum.join(', ')}` 
      };
    }

    return { valid: true };
  }
};

/**
 * Validate a single environment variable
 */
function validateVariable(name, value, schema) {
  const config = schema[name];
  if (!config) {
    return { valid: true, warning: `Unknown environment variable: ${name}` };
  }

  // Check if variable is required
  if (config.required && (value === undefined || value === null || value === '')) {
    return {
      valid: false,
      error: `Required environment variable ${name} is missing`,
      suggestion: config.description
    };
  }

  // Use default value if provided and value is missing
  if ((value === undefined || value === null || value === '') && config.default !== undefined) {
    value = String(config.default);
  }

  // Skip validation if value is still empty and not required
  if (!value && !config.required) {
    return { valid: true, skipped: true };
  }

  // Validate the value
  const validator = validators[config.type];
  if (!validator) {
    return { valid: false, error: `Unknown validation type: ${config.type}` };
  }

  const result = validator(value, config.validation);
  if (!result.valid) {
    return {
      valid: false,
      error: `${name}: ${result.error}`,
      suggestion: config.description,
      value: value
    };
  }

  return {
    valid: true,
    value: result.value !== undefined ? result.value : value
  };
}

/**
 * Main validation function for frontend environment
 */
function validateFrontendEnvironment(env = process.env, options = {}) {
  const environment = options.environment || env.NODE_ENV || env.VUE_APP_ENV || 'development';
  const schema = ENVIRONMENT_SCHEMAS[environment] || ENVIRONMENT_SCHEMAS.development;
  
  const results = {
    valid: true,
    environment,
    errors: [],
    warnings: [],
    processedEnv: {},
    summary: {
      total: 0,
      validated: 0,
      skipped: 0,
      failed: 0,
      warnings: 0
    }
  };

  // Validate individual variables
  for (const [name, config] of Object.entries(schema)) {
    // Skip variables not relevant for current environment
    if (config.environments && !config.environments.includes(environment)) {
      continue;
    }

    results.summary.total++;
    const value = env[name];
    const validation = validateVariable(name, value, schema);

    if (validation.valid) {
      if (validation.skipped) {
        results.summary.skipped++;
      } else {
        results.summary.validated++;
        results.processedEnv[name] = validation.value;
      }

      if (validation.warning) {
        results.warnings.push({
          type: 'validation_warning',
          variable: name,
          warning: validation.warning
        });
        results.summary.warnings++;
      }
    } else {
      results.valid = false;
      results.summary.failed++;
      results.errors.push({
        type: 'validation_error',
        variable: name,
        error: validation.error,
        suggestion: validation.suggestion,
        value: validation.value
      });
    }
  }

  return results;
}

/**
 * Format validation results for display
 */
function formatValidationResults(results, options = {}) {
  const { showWarnings = true, showSummary = true } = options;
  const lines = [];

  // Header
  lines.push('Frontend Environment Validation Results');
  lines.push('=' .repeat(45));
  lines.push(`Environment: ${results.environment}`);
  lines.push(`Status: ${results.valid ? 'VALID' : 'INVALID'}`);
  lines.push('');

  // Errors
  if (results.errors.length > 0) {
    lines.push('ERRORS:');
    lines.push('-'.repeat(20));
    results.errors.forEach(error => {
      lines.push(`❌ ${error.error}`);
      if (error.suggestion) {
        lines.push(`   💡 ${error.suggestion}`);
      }
      lines.push('');
    });
  }

  // Warnings
  if (showWarnings && results.warnings.length > 0) {
    lines.push('WARNINGS:');
    lines.push('-'.repeat(20));
    results.warnings.forEach(warning => {
      lines.push(`⚠️  ${warning.variable}: ${warning.warning}`);
      lines.push('');
    });
  }

  // Summary
  if (showSummary) {
    lines.push('SUMMARY:');
    lines.push('-'.repeat(20));
    lines.push(`Total variables: ${results.summary.total}`);
    lines.push(`Validated: ${results.summary.validated}`);
    lines.push(`Skipped: ${results.summary.skipped}`);
    lines.push(`Failed: ${results.summary.failed}`);
    lines.push(`Warnings: ${results.summary.warnings}`);
  }

  return lines.join('\n');
}

/**
 * Validate environment and throw on failure
 */
function validateFrontendEnvironmentOrThrow(env = process.env, options = {}) {
  const results = validateFrontendEnvironment(env, options);
  
  if (!results.valid) {
    const formattedResults = formatValidationResults(results, { showWarnings: false });
    const error = new Error('Frontend environment validation failed');
    error.details = { results, formattedResults };
    throw error;
  }

  // Log warnings if present
  if (results.warnings.length > 0 && !options.silent) {
    console.warn('\n' + formatValidationResults({ 
      ...results, 
      valid: true, 
      errors: [] 
    }, { showSummary: false }));
  }

  return results.processedEnv;
}

export {
  validateFrontendEnvironment,
  validateFrontendEnvironmentOrThrow,
  formatValidationResults,
  VALIDATION_TYPES,
  ENVIRONMENTS,
  FRONTEND_SCHEMA,
  ENVIRONMENT_SCHEMAS
};